//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { KeyboardEvent, useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { BodyText } from '@components/atoms/text/Text';
import TextInput from '@components/atoms/text-input/TextInput';
import { KEYS } from '@constants/keyboard';
import { keyEquals } from '@utils/keyboardUtils';

const LABEL_INPUT_ID = 'labels-management-change-label-input';

export interface LabelInputMatchProps {
  label: string;
  labelAction: 'update' | 'delete';
  onCancel: () => void;
  onComplete: () => void;
  setIsNameMatching: (isNameMatching: boolean) => void;
}

export const LabelInputMatch = ({
  label,
  labelAction,
  onCancel,
  onComplete,
  setIsNameMatching,
}: LabelInputMatchProps) => {
  const [confirmedLabel, setConfirmedLabel] = useState('');

  const handleKeyUp = (e: KeyboardEvent) => {
    if (keyEquals(e, KEYS.ENTER)) {
      onComplete();
    } else if (keyEquals(e, KEYS.ESC)) {
      onCancel();
    }
  };

  return (
    <>
      <LabelNameInputWrapper>
        <Label htmlFor={LABEL_INPUT_ID}>
          <BodyText>
            In order to {labelAction} your label{' '}
            <StyledText>"{label}"</StyledText>, please type the label "{label}"{' '}
            below:
          </BodyText>
        </Label>
        <TextInput
          id={LABEL_INPUT_ID}
          onKeyUp={handleKeyUp}
          onChange={(value: string) => {
            setConfirmedLabel(value);
            setIsNameMatching(value === label);
          }}
          placeholder={`Enter label name: ${label}`}
          type='text'
          value={confirmedLabel}
          autoFocus
        />
      </LabelNameInputWrapper>
      <Alarm>
        <RedAlarm name='alarm' /> Warning: This action cannot be undone.
      </Alarm>
    </>
  );
};

const LabelNameInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacings[2]};
`;

const Label = styled.label`
  margin-bottom: ${({ theme }) => theme.spacings[1]};
`;

const StyledText = styled.span(
  ({ theme }) => `
  display: inline;
  width: fit-content;
  font-weight: ${theme.fontWeights.bold};
`,
);

const Alarm = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    margin: ${theme.spacings[2]};
  `,
);

const RedAlarm = styled(Icon)(
  ({ theme }) => `
  color: ${theme.colors.red8};
  margin-right: ${theme.spacings[2]};
`,
);
